/**
 * Centralized naming convention for newly created sections (via "+ Add Section").
 *
 * Important:
 * - Keep this intentionally small and stable.
 * - Do NOT overload this with other naming behaviors (e.g., "populate on empty", "save fallback").
 *   Those flows have different UX requirements and are handled elsewhere.
 */

export const DEFAULT_NEW_SECTION_NAME = "Untitled";

/**
 * Returns the default section name to use when the user clicks "+ Add Section".
 * We intentionally do NOT append an index to keep the default consistent and minimal.
 */
export function getDefaultNewSectionName(): string {
  return DEFAULT_NEW_SECTION_NAME;
}

/**
 * Normalize a potentially-empty user-entered section name.
 * - If empty after trim -> returns "Untitled"
 * - Otherwise -> returns the original value, hard-capped to maxLength
 *
 * Note: We keep the original (untrimmed) value when non-empty to avoid surprising
 * users while typing (some callers may want to preserve leading/trailing spaces).
 */
export function normalizeSectionNameOrDefault(
  value: string | null | undefined,
  opts?: { maxLength?: number }
): string {
  const maxLength =
    typeof opts?.maxLength === "number" && Number.isFinite(opts.maxLength) && opts.maxLength > 0
      ? opts.maxLength
      : 190;

  const raw = String(value ?? "");
  const trimmed = raw.trim();
  if (!trimmed) return DEFAULT_NEW_SECTION_NAME;
  return raw.slice(0, maxLength);
}


